import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMonetization } from '../contexts/MonetizationContext';
import { useAuth } from '../contexts/AuthContext';
import QuizService from '../services/QuizService';

const FloatingMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [quizCount, setQuizCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { isPremium, subscriptionPlan } = useMonetization();
  const { api, user } = useAuth();

  // Charger le nombre de quiz disponibles
  useEffect(() => {
    const fetchQuizCount = async () => {
      if (api) {
        const quizService = QuizService.getInstance();
        quizService.setApi(api);
        const count = await quizService.getTotalAvailableCount();
        setQuizCount(count);
      }
    };
    fetchQuizCount();
    
    // Rafraîchir périodiquement
    const interval = setInterval(fetchQuizCount, 30000);
    return () => clearInterval(interval);
  }, [api]);

  // Menu items principaux (couleurs alignées avec l'app mobile)
  const mainMenuItems = [
    { 
      id: 'chat', 
      icon: '💬', 
      label: 'Pourquoi', 
      path: '/chat',
      gradient: 'from-[#4A90D9] to-[#3B82F6]'
    },
    { 
      id: 'quiz', 
      icon: '❓', 
      label: 'Quiz', 
      path: '/quiz',
      gradient: 'from-[#9B59B6] to-[#8E44AD]',
      badge: quizCount
    },
    { 
      id: 'story', 
      icon: '📖', 
      label: 'Raconte', 
      path: '/story',
      gradient: 'from-[#4ECDC4] to-[#0D9488]'
    },
    { 
      id: 'history', 
      icon: '🕐', 
      label: 'Historique', 
      path: '/history',
      gradient: 'from-[#52B788] to-[#059669]'
    },
    { 
      id: 'children', 
      icon: '👶', 
      label: 'Enfants', 
      path: '/children',
      gradient: 'from-[#F59E0B] to-[#D97706]'
    },
  ];

  // Vérifier si l'utilisateur est admin
  const isAdmin = user?.is_admin || user?.email === 'contact@dismaman.com';

  // Bouton Admin (au-dessus si admin)
  const adminItem = isAdmin ? { 
    id: 'admin', 
    icon: '🔧', 
    label: 'Admin', 
    path: '/admin',
    gradient: 'from-[#EF4444] to-[#DC2626]'
  } : null;

  const adminStoryItem = isAdmin ? {
    id: 'admin-story-v2',
    icon: '📖',
    label: 'Histoires V2',
    path: '/admin/story-v2',
    gradient: 'from-[#8B5CF6] to-[#6D28D9]'
  } : null;

  // Bouton Settings/Abonnement au-dessus
  const settingsItem = { 
    id: 'settings', 
    icon: '⚙️', 
    label: 'Paramètres', 
    path: '/subscription',
    gradient: 'from-[#60A5FA] to-[#3B82F6]'
  };

  const handleNavigate = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="fixed bottom-6 left-5 z-50">
      {/* Menu items - apparaissent quand ouvert */}
      <div 
        className={`flex flex-col items-start space-y-2 transition-all duration-300 ease-out ${
          isOpen 
            ? 'opacity-100 translate-x-0' 
            : 'opacity-0 -translate-x-4 pointer-events-none'
        }`}
        style={{ marginLeft: '60px', marginBottom: '8px', maxWidth: 'calc(100vw - 100px)' }}
      >
        {/* Rangée supérieure: Admin (si admin) + Settings */}
        <div className="flex items-center space-x-2 flex-wrap">
          {/* Bouton Admin - au-dessus si admin */}
          {adminItem && (
            <button
              onClick={() => handleNavigate(adminItem.path)}
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl shadow-lg 
                bg-gradient-to-br ${adminItem.gradient} text-white
                transform transition-all duration-300 hover:scale-110 active:scale-95
                ${isActive(adminItem.path) ? 'ring-4 ring-white ring-opacity-50' : ''}
              `}
              style={{ 
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
              }}
              title={adminItem.label}
            >
              {adminItem.icon}
            </button>
          )}

          {adminStoryItem && (
            <button
              onClick={() => handleNavigate(adminStoryItem.path)}
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl shadow-lg
                bg-gradient-to-br ${adminStoryItem.gradient} text-white
                transform transition-all duration-300 hover:scale-110 active:scale-95
                ${isActive(adminStoryItem.path) ? 'ring-4 ring-white ring-opacity-50' : ''}
              `}
              style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}
              title={adminStoryItem.label}
            >
              {adminStoryItem.icon}
            </button>
          )}
          
          {/* Bouton Settings */}
          <button
            onClick={() => handleNavigate(settingsItem.path)}
            className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl shadow-lg 
              bg-gradient-to-br ${settingsItem.gradient} text-white
              transform transition-all duration-300 hover:scale-110 active:scale-95
              ${isActive(settingsItem.path) ? 'ring-4 ring-white ring-opacity-50' : ''}
            `}
            style={{ 
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}
            title={settingsItem.label}
          >
            {settingsItem.icon}
          </button>
        </div>

        {/* Rangée principale des boutons - responsive */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 flex-wrap gap-y-2">
          {mainMenuItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.path)}
              className={`relative w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-base sm:text-xl shadow-lg 
                bg-gradient-to-br ${item.gradient} text-white
                transform transition-all duration-300 hover:scale-110 active:scale-95 flex-shrink-0
                ${isActive(item.path) ? 'ring-4 ring-white ring-opacity-50' : ''}
              `}
              style={{ 
                transitionDelay: `${index * 50}ms`,
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
              }}
              title={item.label}
            >
              {item.icon}
              {/* Badge de notification (style app mobile) */}
              {item.badge > 0 && (
                <span 
                  className="absolute -top-1 -right-1 bg-[#E74C3C] text-white 
                    rounded-full min-w-[18px] sm:min-w-[22px] h-[18px] sm:h-[22px] flex items-center justify-center
                    text-[9px] sm:text-xs font-bold border-2 border-white px-0.5 sm:px-1"
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Bouton burger principal - Animation spin + déploiement conservée */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`absolute bottom-0 left-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white shadow-xl
          transform transition-all duration-300 hover:scale-105 active:scale-95
          ${isOpen 
            ? 'bg-gradient-to-br from-[#4B5563] to-[#1F2937] rotate-90' 
            : 'bg-gradient-to-br from-[#4A90A4] to-[#6BB5A2]'
          }
        `}
        style={{ 
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}
      >
        {/* Pastille sur le bouton burger quand fermé */}
        {!isOpen && quizCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 bg-[#E74C3C] text-white 
              rounded-full min-w-[16px] sm:min-w-[18px] h-[16px] sm:h-[18px] flex items-center justify-center
              text-[9px] sm:text-xs font-bold border-2 border-white"
          >
            {quizCount > 9 ? '9+' : quizCount}
          </span>
        )}
        {isOpen ? (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {/* Overlay pour fermer le menu en cliquant ailleurs */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[-1]" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default FloatingMenu;
